import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#1e3a5f", "#2d6a9f", "#4a9eff", "#6bb8ff", "#a0d4ff", "#f59e0b", "#10b981", "#ef4444"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [txnSummary, setTxnSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchData = useCallback(async () => {
    if (!token) { navigate("/admin"); return; }
    setLoading(true);
    try {
      const endpoints = [
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/businesses`, { headers }),
        fetch(`${API_URL}/api/admin/subscriptions`, { headers }),
        fetch(`${API_URL}/api/admin/transactions-summary`, { headers }),
        fetch(`${API_URL}/api/admin/activity`, { headers }),
      ];
      const [statsRes, usersRes, bizRes, subsRes, txnRes, actRes] = await Promise.all(endpoints);
      if (statsRes.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin"); return; }
      setStats(await statsRes.json());
      setUsers(await usersRes.json());
      setBusinesses(await bizRes.json());
      setSubscriptions(await subsRes.json());
      setTxnSummary(await txnRes.json());
      setActivity(await actRes.json());
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const subBadge = (status) => {
    const map = {
      active: "bg-emerald-50 text-emerald-700 border-emerald-200",
      trialing: "bg-amber-50 text-amber-700 border-amber-200",
      none: "bg-gray-50 text-gray-500 border-gray-200",
      canceled: "bg-red-50 text-red-600 border-red-200",
    };
    return map[status] || map.none;
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: "Leads" },
    { id: "users", label: "Users" },
    { id: "businesses", label: "Businesses" },
    { id: "subscriptions", label: "Subscriptions" },
    { id: "transactions", label: "Transactions" },
    { id: "activity", label: "Activity" },
    { id: "integrations", label: "Integrations" },
    { id: "notifications", label: "Notifications" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#1e3a5f]" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="h-14 bg-white border-b border-[#e4e8ee] flex items-center justify-between px-6 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-[#1e3a5f] flex items-center justify-center">
            <span className="text-white font-bold text-xs">O</span>
          </div>
          <span className="font-semibold text-sm text-[#1e3a5f]">Admin Console</span>
        </div>
        <button onClick={handleLogout} className="text-xs text-[#5a6577] hover:text-[#1e3a5f] transition-colors" data-testid="admin-logout-btn">
          Sign Out
        </button>
      </header>

      <div className="flex">
        {/* Sidebar Tabs */}
        <nav className="w-48 bg-white border-r border-[#e4e8ee] min-h-[calc(100vh-56px)] p-3 space-y-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                tab === t.id ? "bg-[#1e3a5f] text-white font-medium" : "text-[#5a6577] hover:text-[#1e3a5f] hover:bg-[#f5f7fa]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {tab === "overview" && <OverviewTab stats={stats} activity={activity} txnSummary={txnSummary} fmt={fmt} />}
          {tab === "leads" && <LeadsTab headers={headers} />}
          {tab === "users" && <UsersTab users={users} fmtDate={fmtDate} subBadge={subBadge} />}
          {tab === "businesses" && <BusinessesTab businesses={businesses} fmtDate={fmtDate} />}
          {tab === "subscriptions" && <SubscriptionsTab subscriptions={subscriptions} fmtDate={fmtDate} subBadge={subBadge} />}
          {tab === "transactions" && <TransactionsTab txnSummary={txnSummary} fmt={fmt} fmtDate={fmtDate} />}
          {tab === "activity" && <ActivityTab activity={activity} />}
          {tab === "integrations" && <IntegrationsTab headers={headers} />}
          {tab === "notifications" && <NotificationsTab headers={headers} />}
        </main>
      </div>
    </div>
  );
};

// ==================== TAB COMPONENTS ====================

const StatCard = ({ label, value, sub, testId }) => (
  <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]" data-testid={testId}>
    <p className="text-xs text-[#5a6577] mb-1">{label}</p>
    <p className="text-2xl font-bold text-[#1e3a5f]">{value}</p>
    {sub && <p className="text-[11px] text-[#4a9eff] mt-1">{sub}</p>}
  </div>
);

const LeadsTab = ({ headers }) => {
  const [leads, setLeads] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/leads`, { headers });
        if (res.ok) {
          setLeads(await res.json());
        } else {
          console.error("Leads fetch failed:", res.status, await res.text());
        }
      } catch (err) {
        console.error("Failed to fetch leads:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  if (loading) return <div className="text-center py-12 text-[#5a6577]">Loading leads...</div>;
  
  const data = leads || { leads: [], signups_with_utm: [], total_leads: 0, total_signups_from_ads: 0, source_breakdown: {} };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "N/A";

  return (
    <div className="space-y-6" data-testid="admin-leads-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
          <p className="text-xs text-[#5a6577] mb-1">Total Leads</p>
          <p className="text-2xl font-bold text-[#1e3a5f]" data-testid="leads-total">{data.total_leads}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
          <p className="text-xs text-[#5a6577] mb-1">Ad Signups</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="leads-ad-signups">{data.total_signups_from_ads}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
          <p className="text-xs text-[#5a6577] mb-1">From Facebook</p>
          <p className="text-2xl font-bold text-[#1877F2]">{(data.source_breakdown?.facebook || 0) + (data.source_breakdown?.fb || 0) + (data.source_breakdown?.["facebook (signup)"] || 0) + (data.source_breakdown?.["fb (signup)"] || 0)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
          <p className="text-xs text-[#5a6577] mb-1">From Instagram</p>
          <p className="text-2xl font-bold text-[#E4405F]">{(data.source_breakdown?.instagram || 0) + (data.source_breakdown?.ig || 0) + (data.source_breakdown?.["instagram (signup)"] || 0) + (data.source_breakdown?.["ig (signup)"] || 0)}</p>
        </div>
      </div>

      {/* Source Breakdown */}
      {Object.keys(data.source_breakdown).length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
          <h3 className="text-sm font-semibold text-[#1e3a5f] mb-3">Source Breakdown</h3>
          <div className="space-y-2">
            {Object.entries(data.source_breakdown).sort((a, b) => b[1] - a[1]).map(([source, count]) => (
              <div key={source} className="flex items-center justify-between py-1.5 border-b border-[#f0f2f5] last:border-0">
                <span className="text-sm text-[#3a4a5c]">{source}</span>
                <span className="text-sm font-semibold text-[#1e3a5f] bg-[#f0f5ff] px-2 py-0.5 rounded">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
        <div className="p-4 border-b border-[#e4e8ee]">
          <h3 className="text-sm font-semibold text-[#1e3a5f]">All Leads ({data.total_leads})</h3>
        </div>
        {data.leads.length === 0 ? (
          <div className="p-8 text-center text-[#5a6577] text-sm">No leads captured yet. Leads will appear here when users interact with your demo or sign up from ad links.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#f5f7fa] text-[#5a6577] text-xs">
                <tr>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Business</th>
                  <th className="text-left p-3">Industry</th>
                  <th className="text-left p-3">Revenue</th>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Campaign</th>
                  <th className="text-left p-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((lead, i) => (
                  <tr key={lead.email + i} className="border-b border-[#f0f2f5] hover:bg-[#f9fafb]">
                    <td className="p-3 font-medium text-[#1e3a5f]">{lead.email}</td>
                    <td className="p-3 text-[#3a4a5c]">{lead.business_name || "—"}</td>
                    <td className="p-3 text-[#3a4a5c]">{lead.industry || "—"}</td>
                    <td className="p-3 text-[#3a4a5c]">{lead.monthly_revenue ? `$${lead.monthly_revenue.toLocaleString()}/mo` : "—"}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        (lead.utm_source === 'facebook' || lead.utm_source === 'fb') ? 'bg-[#1877F2]/10 text-[#1877F2]' :
                        (lead.utm_source === 'instagram' || lead.utm_source === 'ig') ? 'bg-[#E4405F]/10 text-[#E4405F]' :
                        lead.utm_source ? 'bg-emerald-50 text-emerald-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {lead.utm_source || lead.source || "direct"}
                      </span>
                    </td>
                    <td className="p-3 text-[#5a6577] text-xs">{lead.utm_campaign || "—"}</td>
                    <td className="p-3 text-[#5a6577] text-xs">{fmtDate(lead.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};



const OverviewTab = ({ stats, activity, txnSummary, fmt }) => (
  <div className="space-y-6" data-testid="admin-overview-tab">
    <h2 className="text-lg font-semibold text-[#1e3a5f]">Dashboard Overview</h2>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard label="Total Users" value={stats?.total_users || 0} sub={`+${stats?.new_users_week || 0} this week`} testId="stat-total-users" />
      <StatCard label="Total Businesses" value={stats?.total_businesses || 0} testId="stat-total-businesses" />
      <StatCard label="Active Subscriptions" value={stats?.active_subscriptions || 0} sub={`${stats?.trial_subscriptions || 0} trialing, ${stats?.paid_subscriptions || 0} paid`} testId="stat-active-subs" />
      <StatCard label="Total Transactions" value={stats?.total_transactions || 0} testId="stat-total-txns" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <StatCard label="Total Income (all users)" value={fmt(stats?.income_total)} testId="stat-income" />
      <StatCard label="Total Expenses (all users)" value={fmt(stats?.expense_total)} testId="stat-expenses" />
    </div>

    {activity?.daily_signups?.length > 0 && (
      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">New Signups (Last 30 Days)</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={activity.daily_signups}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5a6577" }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: "#5a6577" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e8ee", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}

    {txnSummary?.by_category?.length > 0 && (
      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">Transaction Categories</p>
        <div className="flex items-center gap-8">
          <ResponsiveContainer width={200} height={200}>
            <PieChart>
              <Pie data={txnSummary.by_category.slice(0, 8)} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                {txnSummary.by_category.slice(0, 8).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e8ee", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 text-xs">
            {txnSummary.by_category.slice(0, 8).map((cat, i) => (
              <div key={cat.category} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="text-[#5a6577]">{cat.category}</span>
                <span className="text-[#1e3a5f] font-medium ml-auto">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

const UsersTab = ({ users, fmtDate, subBadge }) => (
  <div className="space-y-4" data-testid="admin-users-tab">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-[#1e3a5f]">All Users</h2>
      <span className="text-xs text-[#5a6577]">{users.length} total</span>
    </div>
    <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-[#e4e8ee] bg-[#f5f7fa]">
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Signed Up</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Businesses</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Transactions</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Subscription</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#e4e8ee] hover:bg-[#f5f7fa] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1e3a5f]">{u.full_name || "—"}</td>
                <td className="px-4 py-3 text-[#5a6577]">{u.email}</td>
                <td className="px-4 py-3 text-[#8b95a5] text-xs">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-[#1e3a5f]">{u.business_count}</td>
                <td className="px-4 py-3 text-[#1e3a5f]">{u.transaction_count}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${subBadge(u.subscription_status)}`}>
                    {u.subscription_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const BusinessesTab = ({ businesses, fmtDate }) => (
  <div className="space-y-4" data-testid="admin-businesses-tab">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-[#1e3a5f]">All Businesses</h2>
      <span className="text-xs text-[#5a6577]">{businesses.length} total</span>
    </div>
    <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-businesses-table">
          <thead>
            <tr className="border-b border-[#e4e8ee] bg-[#f5f7fa]">
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Business Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Owner</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Owner Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Created</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Transactions</th>
            </tr>
          </thead>
          <tbody>
            {businesses.map((b) => (
              <tr key={b.id} className="border-b border-[#e4e8ee] hover:bg-[#f5f7fa] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1e3a5f]">{b.name}</td>
                <td className="px-4 py-3 text-[#1e3a5f]">{b.owner_name}</td>
                <td className="px-4 py-3 text-[#5a6577]">{b.owner_email}</td>
                <td className="px-4 py-3 text-[#8b95a5] text-xs">{fmtDate(b.created_at)}</td>
                <td className="px-4 py-3 text-[#1e3a5f]">{b.transaction_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const SubscriptionsTab = ({ subscriptions, fmtDate, subBadge }) => (
  <div className="space-y-4" data-testid="admin-subscriptions-tab">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-[#1e3a5f]">Subscriptions</h2>
      <span className="text-xs text-[#5a6577]">{subscriptions.length} total</span>
    </div>
    {subscriptions.length > 0 ? (
      <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-subscriptions-table">
            <thead>
              <tr className="border-b border-[#e4e8ee] bg-[#f5f7fa]">
                <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-[#5a6577]">Created</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((s, i) => (
                <tr key={i} className="border-b border-[#e4e8ee] hover:bg-[#f5f7fa] transition-colors">
                  <td className="px-4 py-3 font-medium text-[#1e3a5f]">{s.user_name}</td>
                  <td className="px-4 py-3 text-[#5a6577]">{s.user_email}</td>
                  <td className="px-4 py-3 text-[#1e3a5f]">{s.plan || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${subBadge(s.status)}`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3 text-[#8b95a5] text-xs">{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : (
      <div className="bg-white rounded-xl p-8 border border-[#e4e8ee] text-center text-[#5a6577] text-sm">
        No subscriptions yet.
      </div>
    )}
  </div>
);

const TransactionsTab = ({ txnSummary, fmt, fmtDate }) => (
  <div className="space-y-4" data-testid="admin-transactions-tab">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-[#1e3a5f]">Transactions Overview</h2>
      <span className="text-xs text-[#5a6577]">{txnSummary?.total || 0} total</span>
    </div>

    {txnSummary?.by_source && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(txnSummary.by_source).map(([src, count]) => (
          <div key={src} className="bg-white rounded-xl p-4 border border-[#e4e8ee]">
            <p className="text-xs text-[#5a6577] mb-1">{src}</p>
            <p className="text-xl font-bold text-[#1e3a5f]">{count}</p>
          </div>
        ))}
      </div>
    )}

    {txnSummary?.by_category?.length > 0 && (
      <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e4e8ee] bg-[#f5f7fa]">
          <p className="text-sm font-medium text-[#1e3a5f]">By Category</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e4e8ee]">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Count</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {txnSummary.by_category.map((c) => (
                <tr key={c.category} className="border-b border-[#e4e8ee]">
                  <td className="px-4 py-2.5 font-medium text-[#1e3a5f]">{c.category}</td>
                  <td className="px-4 py-2.5 text-[#5a6577]">{c.count}</td>
                  <td className="px-4 py-2.5 text-[#1e3a5f]">{fmt(c.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {txnSummary?.recent_transactions?.length > 0 && (
      <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e4e8ee] bg-[#f5f7fa]">
          <p className="text-sm font-medium text-[#1e3a5f]">Recent Transactions (all users)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e4e8ee]">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Description</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Amount</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-[#5a6577]">Source</th>
              </tr>
            </thead>
            <tbody>
              {txnSummary.recent_transactions.map((t, i) => (
                <tr key={i} className="border-b border-[#e4e8ee]">
                  <td className="px-4 py-2.5 text-[#8b95a5] text-xs">{t.date || "—"}</td>
                  <td className="px-4 py-2.5 font-medium text-[#1e3a5f] truncate max-w-[200px]">{t.description || "—"}</td>
                  <td className={`px-4 py-2.5 ${t.direction === "income" ? "text-emerald-600" : "text-[#1e3a5f]"}`}>
                    {t.direction === "income" ? "+" : "-"}{fmt(t.amount)}
                  </td>
                  <td className="px-4 py-2.5 text-[#5a6577]">{t.category || "—"}</td>
                  <td className="px-4 py-2.5 text-[#8b95a5] text-xs">{t.source || "manual"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

const ActivityTab = ({ activity }) => (
  <div className="space-y-6" data-testid="admin-activity-tab">
    <h2 className="text-lg font-semibold text-[#1e3a5f]">User Activity</h2>

    <StatCard label="Total Users" value={activity?.total_users || 0} testId="stat-activity-total" />

    {activity?.daily_signups?.length > 0 && (
      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">Daily Signups (Last 30 Days)</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={activity.daily_signups}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5a6577" }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: "#5a6577" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e8ee", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}

    {activity?.monthly_signups?.length > 0 && (
      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">Monthly Signups (Last 12 Months)</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={activity.monthly_signups}>
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5a6577" }} />
            <YAxis tick={{ fontSize: 10, fill: "#5a6577" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e8ee", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" fill="#4a9eff" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);


const IntegrationsTab = ({ headers }) => {
  const [settings, setSettings] = React.useState({ webhook_url: "", webhook_enabled: false, webhook_secret: "" });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/webhooks`, { headers });
        if (res.ok) setSettings(await res.json());
      } catch (err) {
        console.error("Failed to fetch webhook settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/webhooks`, {
        method: "PUT", headers,
        body: JSON.stringify(settings),
      });
      if (res.ok) alert("Webhook settings saved!");
    } catch (err) {
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/webhooks/test`, { method: "POST", headers });
      if (res.ok) alert("Test webhook sent! Check your n8n workflow.");
      else alert("Test failed — check your webhook URL");
    } catch (err) {
      alert("Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-[#5a6577]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="admin-integrations-tab">
      <div className="bg-white rounded-xl border border-[#e4e8ee] overflow-hidden">
        <div className="p-5 border-b border-[#e4e8ee]">
          <h3 className="text-base font-semibold text-[#1e3a5f]">Webhook Configuration</h3>
          <p className="text-xs text-[#5a6577] mt-1">Send real-time events to n8n, Zapier, or any webhook endpoint</p>
        </div>
        <div className="p-5 space-y-5">
          {/* Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#f5f7fa]">
            <div>
              <p className="text-sm font-medium text-[#1e3a5f]">Enable Webhooks</p>
              <p className="text-xs text-[#5a6577]">Send events when users sign up, redeem pilot codes, or try the demo</p>
            </div>
            <button
              onClick={() => setSettings(s => ({ ...s, webhook_enabled: !s.webhook_enabled }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${settings.webhook_enabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
              data-testid="webhook-toggle"
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.webhook_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Webhook URL */}
          <div>
            <label className="block text-sm font-medium text-[#3a4a5c] mb-1.5">Webhook URL</label>
            <input
              type="url"
              value={settings.webhook_url}
              onChange={(e) => setSettings(s => ({ ...s, webhook_url: e.target.value }))}
              placeholder="https://opturna.app.n8n.cloud/webhook/opturna-signup"
              className="w-full px-3 py-2.5 rounded-lg border border-[#d1d5db] text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none"
              data-testid="webhook-url-input"
            />
          </div>

          {/* Secret Key */}
          <div>
            <label className="block text-sm font-medium text-[#3a4a5c] mb-1.5">Secret Key <span className="text-[#5a6577] font-normal">(sent as X-Webhook-Secret header)</span></label>
            <input
              type="text"
              value={settings.webhook_secret}
              onChange={(e) => setSettings(s => ({ ...s, webhook_secret: e.target.value }))}
              placeholder="your-secret-key (optional)"
              className="w-full px-3 py-2.5 rounded-lg border border-[#d1d5db] text-sm font-mono focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none"
              data-testid="webhook-secret-input"
            />
          </div>

          {/* Events info */}
          <div className="p-4 rounded-lg bg-[#f0f5ff] border border-[#d0dff5]">
            <p className="text-sm font-medium text-[#1e3a5f] mb-2">Events sent to your webhook:</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-[#3a4a5c]">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <code className="text-xs bg-white px-1.5 py-0.5 rounded">signup</code> — New account created (name, email, signup date)
              </div>
              <div className="flex items-center gap-2 text-sm text-[#3a4a5c]">
                <span className="w-2 h-2 rounded-full bg-[#1e3a5f]"></span>
                <code className="text-xs bg-white px-1.5 py-0.5 rounded">pilot_redeemed</code> — Pilot code activated (name, email, code, plan, expires)
              </div>
              <div className="flex items-center gap-2 text-sm text-[#3a4a5c]">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <code className="text-xs bg-white px-1.5 py-0.5 rounded">demo_lead</code> — Demo email captured (email, business, industry, UTM source)
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1e3a5f] text-white rounded-lg text-sm font-medium hover:bg-[#2d5a8e] disabled:opacity-50"
              data-testid="webhook-save-btn"
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !settings.webhook_url || !settings.webhook_enabled}
              className="px-6 py-2.5 bg-white border border-[#d1d5db] text-[#3a4a5c] rounded-lg text-sm font-medium hover:bg-[#f5f7fa] disabled:opacity-50"
              data-testid="webhook-test-btn"
            >
              {testing ? "Sending..." : "Send Test Event"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const NotificationsTab = ({ headers }) => {
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/notification-settings`, { headers })
      .then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const handleSendSummary = async () => {
    setSending(true);
    setSent(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-daily-summary`, { method: "POST", headers });
      if (res.ok) setSent(true);
    } catch {}
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      <h2 className="text-lg font-semibold text-[#1e3a5f]">Email Notifications</h2>

      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">Configuration</p>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-[#e4e8ee]">
              <td className="py-3 text-[#5a6577]">Notification Email</td>
              <td className="py-3 text-right font-medium text-[#1e3a5f]">{settings?.notify_email || "—"}</td>
            </tr>
            <tr className="border-b border-[#e4e8ee]">
              <td className="py-3 text-[#5a6577]">Sender</td>
              <td className="py-3 text-right font-medium text-[#1e3a5f]">{settings?.sender_email || "—"}</td>
            </tr>
            <tr className="border-b border-[#e4e8ee]">
              <td className="py-3 text-[#5a6577]">Daily Summary</td>
              <td className="py-3 text-right">
                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Active — 9 AM UTC</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-4">Instant Alerts</p>
        <div className="space-y-3">
          {[
            { label: "New User Signup", desc: "Get notified every time someone creates an account" },
            { label: "Subscription Change", desc: "Trial to paid, cancellation, or plan change" },
          ].map((alert) => (
            <div key={alert.label} className="flex items-center justify-between p-3 rounded-lg bg-[#f5f7fa] border border-[#e4e8ee]">
              <div>
                <p className="text-sm font-medium text-[#1e3a5f]">{alert.label}</p>
                <p className="text-xs text-[#5a6577] mt-0.5">{alert.desc}</p>
              </div>
              <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">On</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#e4e8ee]">
        <p className="text-sm font-medium text-[#1e3a5f] mb-2">Manual Actions</p>
        <p className="text-xs text-[#5a6577] mb-4">Send a daily summary email right now to test or review.</p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSendSummary}
            disabled={sending}
            className="px-4 py-2 rounded-lg bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="send-daily-summary-btn"
          >
            {sending ? "Sending..." : "Send Daily Summary Now"}
          </button>
          {sent && <span className="text-xs text-emerald-600" data-testid="summary-sent-msg">Sent to {settings?.notify_email}</span>}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
